import { useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildings } from '@rtkwlf/pro-solid-svg-icons/faBuildings';
import { faCircleNodes } from '@rtkwlf/pro-solid-svg-icons/faCircleNodes';
import { faCrosshairs } from '@rtkwlf/pro-solid-svg-icons/faCrosshairs';
import { faGaugeMax } from '@rtkwlf/pro-solid-svg-icons/faGaugeMax';
import { faQuestionCircle } from '@rtkwlf/pro-solid-svg-icons/faQuestionCircle';
import { faSensorOn } from '@rtkwlf/pro-solid-svg-icons/faSensorOn';
import { faShieldHalved } from '@rtkwlf/pro-solid-svg-icons/faShieldHalved';
import { faToolbox } from '@rtkwlf/pro-solid-svg-icons/faToolbox';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import pickBy from 'lodash/pickBy';
import { useTranslation } from 'react-i18next';

import type { FeatureConfig } from '@platform/client/features';
import type { FeatureFlags } from '@platform/core/feature-flags';

import { IncidentResponseIcon } from '@packages/components/icons/incident-response-icon';
import { ManagedDetectionResponseIcon } from '@packages/components/icons/managed-detection-response-icon';
import { ManagedRiskIcon } from '@packages/components/icons/managed-risk-icon';
import type {
  DashboardLayoutProps,
  RouteMenuItem,
} from '@packages/fenrir/dashboard/dashboard-layout';

import { useOrganizationAccess } from '../use-organization-access';

const getEmptyOrderedMenu = (): Record<
  string,
  Record<string, RouteMenuItem | null>
> => ({
  reporting: {
    dashboardManagement: null,
    reportSchedules: null,
    reportHistory: null,
  },
  securityJourney: {
    securityJourneyOverview: null, // Does not exist yet
    securityFocus: null,
    cyberResilienceAssessment: null,
    fileRepository: null,
    collaborators: null,
    spidrLibrary: null,
  },
  dataExploration: {
    dataExplorer: null,
    rawLogSearch: null,
    rawLogSearchHistory: null,
    rawLogSources: null,
    flowData: null,
  },
  threatIntelligence: {
    threatReportAndWebinars: null,
    threatIntelligenceData: null, // Does not exist yet
    stixTaxiiFeeds: null,
    iocBlocklists: null,
  },
  managedDetectionResponse: {
    mdrOverview: null, // Does not exist yet
    detectionCatalog: null,
    securityInvestigations: null,
  },
  incidentResponse: {
    irOverview: null, // Does not exist yet
    irPlanner: null,
    fileRepository: null, // Does not exist yet
  },
  riskManagement: {
    riskExposureOverview: null, // Does not exist yet
    risks: null, // Does not exist yet
    threats: null, // Does not exist yet
    riskCatalog: null,
    legacyRiskDashboard: null,
    viewAllRisks: null,
    viewMyAssets: null,
    riskScoreOverview: null,
    externalVulnerabilityAssessment: null,
  },
  ticketsAlerts: {
    allTickets: null,
    customAlerts: null,
    securityInvestigations: null,
    sipdrs: null, // Does not exist yet
    technicalSupport: null,
  },
  dataCollection: {
    dataCollectionOverview: null,
    sensors: null,
    scanners: null,
    scanSchedules: null, // didn't appear in figma
    agents: null,
    cloudSensors: null,
    cloudScanners: null,
    assets: null,
    logSources: null,
    virtualAppliances: null,
  },
  resources: {
    documentation: null,
    customerCommunity: null,
    unifiedPortalUserGuide: null,
    allowlist: null,
    downloads: null,
  },
  orgProfile: {
    organizationOverview: null, // Does not exist yet
    onboarding: null,
    integrations: null,
    policies: null, // Does not exist yet
    alertConfigurationRules: null,
    tagManagement: null,
    accessControl: null,
    savedFilters: null,

    // didn't appear in figma
    contacts: null,
    escalations: null,
    federatedAuth: null,
    sitesDirectory: null,
  },
});

export const useNavigation = (
  features: Pick<FeatureConfig, 'navigation'>[],
  featureFlags: FeatureFlags,
) => {
  const { t } = useTranslation('app');
  const { data: access, isLoading } = useOrganizationAccess();
  const routePrefix = '/:organizationId';

  const navItems = useMemo<DashboardLayoutProps['navigation']>(() => {
    const emptyOrderedMenu = getEmptyOrderedMenu();

    if (isLoading)
      return {
        routePrefix,
        primaryMenu: [],
        secondaryMenu: [],
      };

    emptyOrderedMenu.resources.documentation = {
      title: t('navigation.documentation'),
      externalUrl: 'https://docs.arcticwolf.com/',
    };

    emptyOrderedMenu.resources.unifiedPortalUserGuide = {
      title: t('navigation.unifiedPortalUserGuide'),
      externalUrl:
        'https://docs.arcticwolf.com/csh?topicname=arctic_wolf_unified_portal.html',
    };

    if (
      access.organization?.customerID &&
      access.legacyCapabilities?.canViewVirtualAppliances
    ) {
      emptyOrderedMenu.dataCollection.virtualAppliances = {
        title: t('navigation.virtualAppliances'),
        externalUrl: `https://portal.arcticwolf.com/dashboard/sensors/virtual?cid=${access.organization?.customerID}`,
      };
    }

    if (access.legacyCapabilities?.canAccessCustomerCommunity) {
      emptyOrderedMenu.resources.customerCommunity = {
        title: t('navigation.customerCommunity'),
        externalUrl: 'https://community.arcticwolf.com/private/login',
      };
    }

    const populatedOrderedMenu = mapValues(
      features.reduce(
        (menu, feature) =>
          merge(menu, feature.navigation({ ...access, featureFlags })),
        emptyOrderedMenu,
      ),
      (subMenu) => pickBy(subMenu, (subMenu) => subMenu !== null),
    );

    return {
      routePrefix,
      primaryMenu: [
        {
          title: t('navigation.ticketsAlerts'),
          icon: <FontAwesomeIcon icon={faToolbox} />,
          children: populatedOrderedMenu.ticketsAlerts,
        },
        {
          title: t('navigation.reporting'),
          icon: <FontAwesomeIcon icon={faGaugeMax} />,
          children: populatedOrderedMenu.reporting,
        },
        {
          title: t('navigation.securityJourney'),
          icon: <FontAwesomeIcon icon={faShieldHalved} />,
          children: populatedOrderedMenu.securityJourney,
        },
        {
          title: t('navigation.dataExploration'),
          icon: <FontAwesomeIcon icon={faCrosshairs} />,
          children: populatedOrderedMenu.dataExploration,
        },
        {
          title: t('navigation.threatIntelligence'),
          icon: (
            <FontAwesomeIcon
              icon={faCircleNodes}
              color="var(--aw-palette-promo-main)"
            />
          ),
          children: populatedOrderedMenu.threatIntelligence,
        },
        {
          title: t('navigation.managedDetectionResponse'),
          icon: (
            <ManagedDetectionResponseIcon color="var(--aw-palette-promo-main)" />
          ),
          children: populatedOrderedMenu.managedDetectionResponse,
        },
        {
          title: t('navigation.incidentResponse'),
          icon: <IncidentResponseIcon color="var(--aw-palette-promo-main)" />,
          children: populatedOrderedMenu.incidentResponse,
        },
        {
          title: t('navigation.riskManagement'),
          icon: <ManagedRiskIcon color="var(--aw-palette-promo-main)" />,
          children: populatedOrderedMenu.riskManagement,
        },
      ]
        .map((menuItem) => ({
          ...menuItem,
          children: Object.values(menuItem.children),
        }))
        .filter((menuItem) => menuItem.children.length > 0),
      secondaryMenu: [
        {
          title: t('navigation.dataCollection'),
          icon: <FontAwesomeIcon icon={faSensorOn} size="lg" />,
          children: populatedOrderedMenu.dataCollection,
        },
        {
          title: t('navigation.resources'),
          icon: <FontAwesomeIcon icon={faQuestionCircle} />,
          children: populatedOrderedMenu.resources,
        },
        {
          title: t('navigation.orgProfile'),
          icon: <FontAwesomeIcon icon={faBuildings} />,
          children: populatedOrderedMenu.orgProfile,
        },
      ]
        .map((menuItem) => ({
          ...menuItem,
          children: Object.values(menuItem.children),
        }))
        .filter((menuItem) => menuItem.children.length > 0),
    };
  }, [isLoading, featureFlags, access, features, t]);

  return navItems;
};
